/**
 * Categories API services
 */

import apiClient from './client';
import type { Category } from '@/types';

export interface CategoryCreate {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CategoryUpdate {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CategoryWithCount extends Category {
  templateCount: number;
  activeTemplateCount: number;
  pendingCount: number;
  inProgressCount: number;
  completedCount: number;
  overdueCount: number;
  children?: CategoryWithCount[];
}

const transformCategory = (data: any): Category => ({
  id: data.id,
  tenantId: data.tenant_id,
  name: data.name,
  description: data.description,
  color: data.color,
  icon: data.icon,
  sortOrder: data.sort_order,
  isActive: data.is_active,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
});

export const categoriesApi = {
  list: async (params?: { isActive?: boolean; search?: string }): Promise<Category[]> => {
    const response = await apiClient.get('/v1/categories', { params });
    // Handle both array and {items: []} response formats
    const items = Array.isArray(response.data) ? response.data : (response.data.items || []);
    return items.map(transformCategory);
  },

  listWithCounts: async (): Promise<CategoryWithCount[]> => {
    const response = await apiClient.get('/v1/categories/with-counts');
    const items = response.data.items || response.data || [];

    const transformCategoryWithCount = (data: any): CategoryWithCount => ({
      ...transformCategory(data),
      templateCount: data.template_count ?? 0,
      activeTemplateCount: data.active_template_count ?? 0,
      pendingCount: data.pending_count ?? 0,
      inProgressCount: data.in_progress_count ?? 0,
      completedCount: data.completed_count ?? 0,
      overdueCount: data.overdue_count ?? 0,
      children: data.children?.map(transformCategoryWithCount),
    });

    return items.map(transformCategoryWithCount);
  },

  get: async (id: string): Promise<Category> => {
    const response = await apiClient.get(`/v1/categories/${id}`);
    return transformCategory(response.data);
  },

  create: async (data: CategoryCreate): Promise<Category> => {
    const response = await apiClient.post('/v1/categories', {
      name: data.name,
      description: data.description,
      color: data.color || '#1890ff',
      icon: data.icon,
      sort_order: data.sortOrder || 0,
      is_active: data.isActive ?? true,
    });
    return transformCategory(response.data);
  },

  update: async (id: string, data: CategoryUpdate): Promise<Category> => {
    const payload: any = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.description !== undefined) payload.description = data.description;
    if (data.color !== undefined) payload.color = data.color;
    if (data.icon !== undefined) payload.icon = data.icon;
    if (data.sortOrder !== undefined) payload.sort_order = data.sortOrder;
    if (data.isActive !== undefined) payload.is_active = data.isActive;

    const response = await apiClient.patch(`/v1/categories/${id}`, payload);
    return transformCategory(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/categories/${id}`);
  },

  reorder: async (order: string[]): Promise<Category[]> => {
    const response = await apiClient.post('/v1/categories/reorder', order);
    return response.data.map(transformCategory);
  },
};
